import { type ReactElement } from 'react';
import { Icon, type IconName } from '@/components/atoms/icon';
import { cn } from '@/lib/cn';

export type ControlTileAccent = 'ink' | 'warm' | 'brand';

export type ControlTileProps = {
  icon: IconName;
  label: string;
  value: string;
  active?: boolean;
  accent?: ControlTileAccent;
  onClick: () => void;
  expandable?: boolean;
  // Drives aria-expanded — the open state of the drawer this tile controls.
  // Defaults to `active` for tiles whose accent and open state coincide.
  expanded?: boolean;
};

const ACTIVE_TILE: Record<ControlTileAccent, string> = {
  ink: 'border-[var(--ink)] bg-[var(--ink)] text-white',
  warm: 'border-[var(--warm)]/40 bg-[var(--warm-wash)] text-[var(--ink)]',
  brand: 'border-[var(--brand)]/40 bg-[var(--brand-wash)] text-[var(--ink)]',
};

const ACTIVE_ICON: Record<ControlTileAccent, string> = {
  ink: 'bg-white/15 text-white',
  warm: 'bg-[var(--warm)] text-white',
  brand: 'bg-[var(--brand)] text-white',
};

const IDLE_TILE =
  'border-[var(--rule)] bg-[var(--bg-surface)] text-[var(--ink)] hover:border-[var(--rule-strong)]';
const IDLE_ICON = 'bg-[var(--bg-soft)] text-[var(--ink-2)]';

// A practice-controls strip tile (tablet portrait + mobile): icon + mono label +
// value, opening a PracticeBottomSheet. Generic so #382's settings/notes tiles
// reuse it. The value is the collapsed read-out (e.g. "D · playing" / "Off").
export function ControlTile({
  icon,
  label,
  value,
  active = false,
  accent = 'ink',
  onClick,
  expandable = true,
  expanded,
}: ControlTileProps): ReactElement {
  const isExpanded = expanded ?? active;
  const tileClasses = active ? ACTIVE_TILE[accent] : IDLE_TILE;
  const iconClasses = active ? ACTIVE_ICON[accent] : IDLE_ICON;
  const chevronClasses = active && accent === 'ink' ? 'text-white/60' : 'text-[var(--ink-3)]';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expandable ? isExpanded : undefined}
      className={cn(
        'flex items-center gap-3 rounded-[var(--r-md)] border p-3 text-left transition-colors',
        tileClasses,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-sm)]',
          iconClasses,
        )}
      >
        <Icon name={icon} size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-mono text-[10px] uppercase tracking-[0.16em] opacity-80">
          {label}
        </span>
        <span className="mt-0.5 block truncate text-[14px] font-semibold leading-tight">
          {value}
        </span>
      </span>
      {expandable && (
        <Icon
          name="chev_d"
          size={14}
          className={cn('transition-transform', isExpanded && 'rotate-180', chevronClasses)}
        />
      )}
    </button>
  );
}
